import catalogJson from '@zinqpay/contracts/research-catalog.json';
import type { Locale } from './dates';

/**
 * The research catalog (packages/contracts/research-catalog.json, the study's single source of
 * truth): only the parts the results dashboard shows — task and metric titles, thresholds and
 * time budgets. The API computes the numbers; the panel only labels them.
 */
export interface CatalogText {
  mk: string;
  en: string;
}

export interface CatalogTask {
  id: string;
  role: 'USER' | 'MERCHANT';
  key: string;
  title: CatalogText;
  targetSeconds?: number;
}

export interface CatalogMetric {
  id: string;
  title?: CatalogText;
  rule: string;
  threshold?: number;
}

export interface ResearchCatalogView {
  version: number;
  studyLabel: string;
  tasks: CatalogTask[];
  metrics: CatalogMetric[];
}

const raw = catalogJson as unknown as ResearchCatalogView;

export const RESEARCH_CATALOG: ResearchCatalogView = {
  version: raw.version,
  studyLabel: raw.studyLabel,
  tasks: raw.tasks.map((t) => ({
    id: t.id,
    role: t.role,
    key: t.key,
    title: t.title,
    ...(t.targetSeconds !== undefined ? { targetSeconds: t.targetSeconds } : {}),
  })),
  metrics: raw.metrics.map((m) => ({
    id: m.id,
    rule: m.rule,
    ...(m.title ? { title: m.title } : {}),
    ...(m.threshold !== undefined ? { threshold: m.threshold } : {}),
  })),
};

export function catalogText(text: CatalogText | undefined, locale: Locale, fallback: string): string {
  return text?.[locale] ?? fallback;
}

export function findCatalogTask(id: string): CatalogTask | undefined {
  return RESEARCH_CATALOG.tasks.find((t) => t.id === id);
}

export function findCatalogMetric(id: string): CatalogMetric | undefined {
  return RESEARCH_CATALOG.metrics.find((m) => m.id === id);
}
